<script setup lang="ts">
import { onMounted, ref } from 'vue';
import DataView, { type DataViewPageEvent } from 'primevue/dataview';
import { storeToRefs } from 'pinia';
import type { DataTableData } from '@composables/useDataTable.ts';
import { useDataTableStore } from '../../_00_elements/tools/store/pinia/useDataTableStore.ts';

const dataStore = useDataTableStore();

const { data } = storeToRefs(dataStore);
const { getData, onPageChange } = dataStore;

type LayoutType = 'list' | 'grid';

const props = withDefaults(defineProps<{
  dataKey?: string,
  layout?: LayoutType
}>(), {
  dataKey: undefined,
  layout: 'list',
});

const currentLayout = ref<LayoutType>(props.layout);
const totalItems = ref();

const handlePageChange = async (event: DataViewPageEvent): Promise<void> => {
  await onPageChange(event);
};

const fetchData = async (): Promise<void> => {
  const responseData: DataTableData | null = await getData();

  if (responseData) {
    data.value = responseData;
    totalItems.value = responseData.meta?.total ?? 0;
  }
};

onMounted(fetchData);
</script>

<template>
  <div v-if="data" class="dataview">
    <slot v-if="totalItems === 0" name="empty-state" />
    <DataView
      v-else
      :value="data.items ?? []"
      :layout="currentLayout"
      paginator
      :first="data.meta?.from"
      :total-records="data.meta?.total"
      :rows="data.meta?.perPage"
      :data-key="props.dataKey"
      :lazy="true"
      @page="handlePageChange"
    >
      <template #empty>
        <slot name="empty" />
      </template>

      <template v-if="currentLayout === 'list'" #list="{ items: pagedItems }">
        <div
          v-for="(item, index) in pagedItems"
          :key="dataKey ? item?.[dataKey] ?? index : index"
        >
          <slot name="list-item" :item="item" :index="index" />
        </div>
      </template>
    </DataView>
  </div>
</template>
